import {
	autoUpdate,
	flip,
	FloatingPortal,
	offset,
	Placement,
	safePolygon,
	shift,
	useDismiss,
	useFloating,
	useFocus,
	useHover,
	useInteractions,
	useRole,
	useTransitionStyles,
} from '@floating-ui/react'
import ctl from '@netlify/classnames-template-literals'
import { AnimatePresence } from 'motion/react'
import { ReactNode, useState } from 'react'
import { Z_INDEX_TOOLTIP } from './constants'

export function Tooltip(
	{
		children,
		content,
		className,
		wrapperClassName,
		disabled,
		interactive = false,
		placement,
	}: {
		children: ReactNode
		content: ReactNode
		className?: string
		wrapperClassName?: string
		disabled?: boolean
		interactive?: boolean
		placement?: Placement
	},
) {
	const [isOpen, setIsOpen] = useState(false)

	const { refs, context, floatingStyles } = useFloating({
		open: isOpen && !disabled,
		onOpenChange: setIsOpen,
		whileElementsMounted: autoUpdate,
		placement,
		middleware: [offset(3), flip(), shift({ padding: 8 })],
	})

	const hover = useHover(context, interactive ? { handleClose: safePolygon() } : undefined)
	const focus = useFocus(context)
	const role = useRole(context, { role: 'tooltip' })
	const dismiss = useDismiss(context)
	const { isMounted, styles } = useTransitionStyles(context, {
		initial: { opacity: 0, transform: 'translateY(6px)', filter: 'blur(10px)', clipPath: 'inset(100% 0 0 0)' },
		open: { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)', clipPath: 'inset(0 0 0 0)' },
		close: { opacity: 0, transform: 'translateY(-6px)', filter: 'blur(10px)', clipPath: 'inset(0 0 100% 0)' },
		duration: {
			open: 150,
			close: 250,
		},
	})

	const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role, dismiss])

	return (
		<>
			<div ref={refs.setReference} className={ctl(`relative ${wrapperClassName}`)} {...getReferenceProps()}>
				{children}
			</div>
			<FloatingPortal>
				<AnimatePresence>
					{isMounted && (
						<div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: Z_INDEX_TOOLTIP }}>
							<div
								className={interactive ? 'pointer-events-auto' : 'pointer-events-none'}
								style={{
									filter: 'drop-shadow(0 3px 3px rgba(0,0,0,0.1))',
								}}
							>
								<div
									style={{ ...styles, transitionTimingFunction: 'ease-in-out' }}
									{...getFloatingProps()}
									className={ctl(`
                                    relative ${className}
                                    bg-white border-1 border-grey-200 text-grey-800 rounded-sm px-2 py-1 text-xs
                                `)}
								>
									{content}
								</div>
							</div>
						</div>
					)}
				</AnimatePresence>
			</FloatingPortal>
		</>
	)
}
